import { Grid } from '@mui/material'
import FormControlRadio, { radioButtonChoiceOptions } from 'components/FormControlRadio'
import FormInput from 'components/FormInput'
import { FormInputSelect } from 'components/FormInputSelect'
import Label from 'components/Label'
import { FC, useEffect } from 'react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import {
  hearingIdentificationInit,
  hearingIdentificationSchema,
  HearingIdentificationType,
} from 'types/hearing-identification.type'
import { calculateAge } from 'utils/date.util'
import { AddressList } from './components/AddressList'
import { ChildrenGroup } from './components/ChildrenGroup'
import { DocumentList } from './components/DocumentList'
import { InfractionList } from './components/InfractionList'
import { InstitutionallyWelcomedGroup } from './components/InstitutionallyWelcomedGroup'
import { MentalTreatmentGroup } from './components/MentalTreatmentGroup'
import PersonalDataGroup from './components/PersonalDataGroup'
import { PhoneList } from './components/PhoneList'
import { ResponsibleRelationshipGroup } from './components/ResponsibleRelationshipGroup'
import { ResponsibleWorkingGroup } from './components/ResponsibleWorkingGroup'
import { SocialNetworkList } from './components/SocialNetworkList'
import { StudyingGroup } from './components/StudingGroup'
import { SubstancesGroup } from './components/SubstancesGroup'
import { WorkingGroup } from './components/WorkingGroup'
import { useInputSelectIdentification } from './hooks/select-option.hook'
import {
  HearingIdentificationBlockFieldsProvider,
  useHearingIdentificationBlockFieldsContext,
} from 'contexts/hearing-identification-block-fields.context'
import { useOutletContext, useParams } from 'react-router-dom'
import { useHearingIdentification } from 'pages/CreateHearing/components/StepIdentification/hooks/hearing-identification.hook'
import { zodResolver } from '@hookform/resolvers/zod'
import { OutletContextType } from 'pages/CreateHearing'
import { StepIndex } from 'pages/CreateHearing/enums/step.enum'
import FormInputNumber from 'components/FormInputNumber'

export const StepIdentificationPage: FC = () => {
  return (
    <HearingIdentificationBlockFieldsProvider>
      <InternalComponent />
    </HearingIdentificationBlockFieldsProvider>
  )
}

const InternalComponent: FC = () => {
  const { id } = useParams()
  const { formRefs } = useOutletContext<OutletContextType>()

  const form = useForm<HearingIdentificationType>({
    resolver: zodResolver(hearingIdentificationSchema),
    defaultValues: hearingIdentificationInit,
  })

  const {
    control,
    watch,
    setValue,
    setError,
    reset,
    clearErrors,
    handleSubmit,
    formState: { isDirty, dirtyFields },
  } = form

  const { isBlockedFields, handleUnblockAllFields } = useHearingIdentificationBlockFieldsContext()
  const {
    hearingIdentification,
    fillNaturalnessCity,
    saveIdentification,
    naturalnessCityOptions,
    naturalnessStateOptions,
  } = useHearingIdentification(id)

  const inputSelect = useInputSelectIdentification()

  const naturalnessStateIdValue = watch('idEstadoNaturalidade')
  const hasSiblings = watch('identificacaoAdolescente.possuiIrmao')
  const hasInfractionalBackground = watch('identificacaoAdolescente.antecedenteInfracional')
  const birthDayValue = watch('dataNascimento')
  const idPessoaFisica = watch('idPessoaFisica')
  const educationLevel = watch('idGrauEscolaridade')

  const hasDirtyFields = Object.keys(dirtyFields).length > 0 && dirtyFields.constructor === Object

  const onError = (errors: FieldErrors<HearingIdentificationType>) => {
    clearErrors()
    const recursiveSetError = (errors: FieldErrors<HearingIdentificationType>, parentPath = '') => {
      Object.entries(errors).forEach(([key, error]: any) => {
        const path = parentPath ? `${parentPath}.${key}` : key

        if (error?.ref) {
          // Se houver uma referência (ref), usamos o nome da referência para definir o erro
          setError(error.ref.name, { type: error.type, message: error.message })
        } else if (typeof error === 'object') {
          // Recursivamente percorre os objetos internos
          recursiveSetError(error, path)
        }
      })
    }
    recursiveSetError(errors)
    console.log('onError', errors)
    return Promise.reject(errors)
  }

  useEffect(() => {
    if (naturalnessStateIdValue === '0') {
      setValue('idMunicipioNaturalidade', '0', { shouldDirty: false })
    } else {
      fillNaturalnessCity(naturalnessStateIdValue)
    }
  }, [naturalnessStateIdValue])

  useEffect(() => {
    if (birthDayValue) {
      setValue('identificacaoAdolescente.idade', calculateAge(birthDayValue))
    }
  }, [birthDayValue, setValue])

  useEffect(() => {
    if (hasSiblings === 'N') {
      setValue('identificacaoAdolescente.quantidadeIrmao', null)
    }
  }, [hasSiblings])

  useEffect(() => {
    if (hasInfractionalBackground === 'N') {
      setValue('identificacaoAdolescente.atoInfracionais', [])
    }
  }, [hasInfractionalBackground])

  useEffect(() => {
    if (idPessoaFisica) {
      handleUnblockAllFields()
    }
  }, [idPessoaFisica])

  useEffect(() => {
    if (hearingIdentification) {
      reset(hearingIdentification)
    }
  }, [hearingIdentification])

  formRefs.current[StepIndex.IDENTIFICACAO_ADOLESCENTE] = {
    submit: async () => {
      if (isDirty && hasDirtyFields) {
        await handleSubmit(saveIdentification, onError)()
      }
      return Promise.resolve()
    },
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(saveIdentification)}>
        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <PersonalDataGroup
            genderOptions={inputSelect.genderOptions}
            skinOptions={inputSelect.skinOptions}
          />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 4, mb: 4 }}>
          <DocumentList
            control={control}
            watch={watch}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
            typeOptions={inputSelect.docTypeOptions}
          />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 4, mb: 4 }}>
          <AddressList control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 4, mb: 4 }}>
          <PhoneList control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} sx={{ mt: 4, mb: 4 }}>
          <SocialNetworkList control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <Grid item md={6} xs={12}>
            <FormInput
              control={control}
              name='email'
              label='Email do adolescente/representante legal'
              placeholder='Informe o email'
              maxLength={60}
              disabled={isBlockedFields}
            />
          </Grid>
          <Grid item md={3} xs={12}>
            <FormInputSelect
              control={control}
              name='idEstadoNaturalidade'
              label='Naturalidade (Estado)'
              options={naturalnessStateOptions}
              disabled={isBlockedFields}
            />
          </Grid>
          <Grid item md={3} xs={12}>
            <FormInputSelect
              control={control}
              name='idMunicipioNaturalidade'
              label='Naturalidade (Município)'
              disabled={naturalnessStateIdValue === '0' && isBlockedFields}
              options={naturalnessCityOptions}
            />
          </Grid>
          <Grid item md={3} xs={12}>
            <FormInputSelect
              control={control}
              name='idGrauEscolaridade'
              label='Grau de escolaridade'
              options={inputSelect.educationLevelOptions}
              disabled={isBlockedFields}
            />
          </Grid>
          {educationLevel !== '1' && (
            <Grid item md={3} xs={12}>
              <FormInput
                control={control}
                name='identificacaoAdolescente.nomeInstituicaoEnsino'
                label='Instituição de ensino'
                placeholder='Informe o nome da instituição'
                maxLength={60}
                disabled={isBlockedFields}
              />
            </Grid>
          )}
          <Grid item md={3} xs={12}>
            <FormInput
              control={control}
              name='nomeMae'
              label='Nome da mãe'
              maxLength={60}
              placeholder='Informe o nome da mãe'
              disabled={isBlockedFields}
            />
          </Grid>
          <Grid item md={3} xs={12}>
            <FormInput
              control={control}
              name='nomePai'
              label='Nome do pai'
              maxLength={60}
              placeholder='Informe o nome do pai'
              disabled={isBlockedFields}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <StudyingGroup control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <WorkingGroup control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <ResponsibleRelationshipGroup control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <ChildrenGroup control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <Grid item lg={hasSiblings === 'S' ? 3 : 12} xs={12}>
            <FormControlRadio
              control={control}
              name='identificacaoAdolescente.possuiIrmao'
              label='Tem irmãos?'
              options={radioButtonChoiceOptions}
              disabled={isBlockedFields}
            />
          </Grid>
          {hasSiblings === 'S' && (
            <Grid item lg={3} xs={12}>
              <FormInputNumber
                control={control}
                name='identificacaoAdolescente.quantidadeIrmao'
                label='Quantidade?'
                maxLength={2}
                placeholder='Informe a quantidade'
                disabled={isBlockedFields}
              />
            </Grid>
          )}
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <ResponsibleWorkingGroup control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <MentalTreatmentGroup control={control} watch={watch} setValue={setValue} />

          <Grid item md={12}>
            <FormControlRadio
              control={control}
              name='identificacaoAdolescente.situacaoRua'
              label='Está ou já esteve em situação de rua?'
              options={radioButtonChoiceOptions}
              disabled={isBlockedFields}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <SubstancesGroup control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <InstitutionallyWelcomedGroup control={control} watch={watch} setValue={setValue} />
        </Grid>

        <Grid container spacing={2} rowSpacing={4} sx={{ mb: 4 }}>
          <Grid item sm={12}>
            <FormControlRadio
              control={control}
              name='identificacaoAdolescente.antecedenteInfracional'
              label='Antecedentes infracionais?'
              options={radioButtonChoiceOptions}
              disabled={isBlockedFields}
            />
          </Grid>
        </Grid>

        {hasInfractionalBackground === 'S' && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Label
              text='Qual(is) ato(s) infracional(ais)?'
              sx={{ fontSize: 14, color: 'text.primary', ml: 2, mb: 1 }}
            />
            <InfractionList
              control={control}
              watch={watch}
              setValue={setValue}
              typeOptions={inputSelect.infractionOptions}
            />
          </Grid>
        )}
      </form>
    </FormProvider>
  )
}
